import {
  Button,
  Checkbox,
  HStack,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@chakra-ui/react';
import { faFilePdf } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePDF } from '@react-pdf/renderer';
import { isError } from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { Router } from '@ui/config/routes';
import { snapQuoteIndexQueryKeyPrefix } from '@ui/data/snap-quote';
import { PDFDocument } from '@ui/features/quote-builder/components/PDF/PDFDocument';
import {
  SEND_SNAP_QUOTE_MUTATION_KEY,
  useSendSnapQuoteMutation,
} from '@ui/features/quote-builder/state/endpoints/send-snap-quote';
import { selectIssues } from '@ui/features/quote-builder/state/selectors/issue';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { selectQuoteTotal } from '@ui/features/quote-builder/state/selectors/quote-total';
import { mergeFormAndState } from '@ui/features/quote-builder/state/utilities/merge-form-and-state';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useSelector } from '@ui/hooks/useSelector';
import { useValidationToast } from '@ui/hooks/useToast';
import { toBase64 } from '@ui/util/base64';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuoteBuilderDialog } from '../../hooks/useQuoteBuilderDialog';
import { PDFDisplay } from './PDFDisplay';

/**
 * The Quote PDF Modal depends on a PDF instance from @react-pdf/renderer. To
 * facilitate lazy loading, a portion of the inner modal components are split
 * into their own component (this one). It is expected to be dynamically
 * imported and rendered within a Chakra <Modal />.
 *
 * This component is written with the assumption that it will only be displayed
 * after a quote has been validated. It does not account for cases where the
 * `contactEmail` is missing, or there are other unresolved validation errors in
 * the Quote Builder form.
 */
export function PDFDialogContent() {
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentSite = useCurrentSite();
  const { genericSuccessToast, genericErrorToast } = useValidationToast();
  const sendToCustomerRef = useRef<HTMLInputElement>(null);
  const { close } = useQuoteBuilderDialog({ name: 'pdf' });
  const quoteBuilder = useSelector(selectQuoteBuilder);
  const [sendQuote, { isLoading }] = useSendSnapQuoteMutation({
    fixedCacheKey: SEND_SNAP_QUOTE_MUTATION_KEY,
  });
  const quoteIssues = useSelector((state) =>
    selectIssues(state, { area: 'quote' }),
  );

  const formattedQuoteTotal = useSelector(selectQuoteTotal);

  const merged = mergeFormAndState(
    quoteBuilder,
    currentUser,
    quoteBuilder.displayQuantity,
    formattedQuoteTotal,
  );

  const [pdf] = usePDF({
    document: <PDFDocument {...merged} />,
  });

  const pdfIsValidAndLoaded = pdf.url && pdf.blob && !pdf.loading && !pdf.error;

  const emailSendingEnabledForSite = currentSite.useEmailSending;
  const sendEmailInputDisabled = isLoading || !emailSendingEnabledForSite;

  const handleSendQuote = async () => {
    const sendToCustomer = sendToCustomerRef.current?.checked ?? false;

    /**
     * @NOTE(shawk): The send button should be disabled if the PDF is not loaded
     * so this event handler shouldn't be able to be called without a valid PDF
     * but TypeScript is unaware.
     */
    if (!pdf.blob) {
      throw new Error('PDF blob is missing');
    }

    const result = await sendQuote({
      snapQuoteUuid: quoteBuilder.uuid,
      siteCode: quoteBuilder.siteCode,
      sendEmailToCustomer: sendToCustomer,
      quotePdfBase64: await toBase64(pdf.blob),
    });

    if (result.error) {
      // @TODO(shawk): new toast helpers for QBv2?
      return genericErrorToast(
        `Failed to send quote ${quoteBuilder.quoteId}`,
        isError(result.error)
          ? result.error.message
          : 'An unknown error occurred',
        false,
        true,
      );
    }

    queryClient.invalidateQueries({
      queryKey: snapQuoteIndexQueryKeyPrefix,
    });

    genericSuccessToast(
      `Quote ${quoteBuilder.quoteId} sent${
        sendToCustomer ? ' to customer' : ''
      }!`,
      undefined,
      false,
      true,
    );

    // @TODO(shawk): #1747 - feedback is not yet implemented
    // if (response.data.requiresFeedback) {
    //   navigate(
    //     Router.sitePath(currentSite, Router.routes.quoteFeedback, {
    //       id: quoteBuilder.uuid,
    //     }),
    //   );
    // } else {
    //   navigate(Router.sitePath(currentSite, Router.routes.snapQuoteIndex));
    // }

    navigate(Router.sitePath(currentSite, Router.routes.quoteQueue));
  };

  return (
    <ModalContent>
      <ModalHeader>Quote {quoteBuilder.quoteId}</ModalHeader>
      <ModalCloseButton isDisabled={isLoading} />
      <ModalBody>
        <PDFDisplay quoteId={quoteBuilder.quoteId} pdf={pdf} />
      </ModalBody>

      <ModalFooter justifyContent="start">
        <HStack width="33%" justifySelf="start">
          <Checkbox
            ref={sendToCustomerRef}
            isDisabled={sendEmailInputDisabled}
            defaultChecked={
              emailSendingEnabledForSite &&
              (currentUser.userSettings?.emailQuoteToCustomer ?? false)
            }
          >
            Send email to customer
          </Checkbox>
        </HStack>

        <HStack width="77%" justifyContent="end">
          <Button
            marginLeft={2}
            gap={2}
            bgColor="#0b7078"
            color="white"
            _hover={{ bg: '#046068', textDecoration: 'none' }}
            _disabled={{ bg: '#aaaaaa' }}
            as={Link}
            href={pdf.url ?? '#'}
            download={`${quoteBuilder.quoteId}-${Date.now()}.pdf`}
            isDisabled={!pdfIsValidAndLoaded || isLoading}
          >
            <FontAwesomeIcon
              icon={faFilePdf}
              fontSize={12}
              aria-label="Download PDF"
            />
            Download PDF
          </Button>
          <Button
            marginLeft={2}
            gap={2}
            bgColor="#0b7078"
            color="white"
            _hover={{ bg: '#046068' }}
            _disabled={{ bg: '#aaaaaa' }}
            onClick={close}
            isDisabled={isLoading}
          >
            Edit Quote
          </Button>
          <Button
            marginLeft={2}
            gap={2}
            bgColor="#0b7078"
            color="white"
            _hover={{ bg: '#046068' }}
            _disabled={{ bg: '#aaaaaa' }}
            onClick={handleSendQuote}
            isDisabled={
              !pdfIsValidAndLoaded ||
              isLoading ||
              quoteBuilder.isUpdating ||
              quoteBuilder.quoteLineUuids.length === 0 ||
              quoteIssues.length > 0
            }
            isLoading={isLoading || quoteBuilder.isUpdating}
          >
            Send to ERP
          </Button>
        </HStack>
      </ModalFooter>
    </ModalContent>
  );
}

export default PDFDialogContent;
